import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { handleNetworkError } from '../utils/api/networkErrors';

const MAX_RETRIES = 3;

export function useSupabaseConnection() {
  const [isConnected, setIsConnected] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Function to check connection status
    const checkConnection = async () => {
      try {
        for (let i = 0; i < MAX_RETRIES; i++) {
          try {
            const { error } = await supabase.from('user_profiles').select('count').limit(1);
            if (!error) {
              if (mounted) {
                setIsConnected(true);
                setIsChecking(false);
              }
              return;
            }
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
          } catch (error) {
            if (i < MAX_RETRIES - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
              continue;
            }
            throw error;
          }
        }
      } catch (error) {
        console.error('Connection error:', error);
        if (mounted) {
          setIsConnected(false);
          handleNetworkError(error);
        }
      } finally {
        if (mounted) {
          setIsChecking(false);
        }
      }
    };

    // Handle online/offline events
    const handleOnline = () => {
      checkConnection();
    };

    const handleOffline = () => {
      setIsConnected(false);
      setIsChecking(false);
    };

    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connection check
    checkConnection();

    return () => {
      mounted = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isConnected, isChecking };
}